import type { PaymentTerms, QuoteDiscount, SchoolDetails } from "@rutamagica/contracts";
import { ApiError, apiRequest, downloadFile } from "../api/client";

import type { PaymentMethod } from "./payment-method";
export type { PaymentMethod } from "./payment-method";
export type CaseKind = "opportunity" | "booking" | "order" | "lead";
export type WorkspaceService = "balloon" | "tour" | "school";
export interface CaseRef {
  kind: CaseKind;
  id: string;
}
export interface CaseEntity {
  id: string;
  sourceId?: string;
  [key: string]: unknown;
}
export interface CaseDossier {
  key: CaseRef;
  service: WorkspaceService | null;
  title: string;
  customer: { id?: string; name: string; organization?: string; email?: string; phone?: string };
  customerContext: { customerId: string | null; description: string };
  status: {
    commercial: string | null;
    payment: string | null;
    operational: string | null;
  };
  nextAction: string;
  nextSection?: string|null;
  alerts?: Array<{code:string;tone:'danger'|'warning'|'info';title:string;detail:string;action:string;section:string}>;
  missing: string[];
  associatedIds: {
    opportunity: string[];
    booking: string[];
    order: string[];
    lead: string[];
    thread: string[];
    quote: string[];
  };
  opportunity: CaseEntity | null;
  bookings: CaseEntity[];
  orders: CaseEntity[];
  leads: CaseEntity[];
  conversations: CaseEntity[];
  payments: CaseEntity[];
  participants: CaseEntity[];
  documents: CaseEntity[];
  quotes: CommerceQuote[];
  followups?: CaseEntity[];
  timeline: CaseEntity[];
  permissions: { finance: boolean; crm: boolean; commerceWrite: boolean };
  commerceSync?: "available" | "unavailable" | "deferred";
  /** WhatsApp conversation shown inside the case: linked thread, else same phone. */
  whatsappConversationId?: string | null;
}
export interface WorkspaceReports {
  period: {
    from: string;
    to: string;
    service: WorkspaceService | null;
    timezone: string;
  };
  commercial: {
    won: number;
    lost: number;
    conversionRate: number | null;
    denominator: number;
    manualWon: number;
    officialWon: number;
  } | null;
  officialReservations: {
    confirmed: number;
    completed: number;
    passengers: number;
  };
  payments:
    | {
        currency: string;
        transactions: number;
        received: number;
        refunded: number;
        net: number;
      }[]
    | null;
  followups: { overdue: number } | null;
  response: { conversations: number; averageMinutes: number | null } | null;
  activity: { action: string; count: number }[];
}
export interface AuthoritativePaymentPlan {
  option?: "full" | "deposit";
  stage?: "full" | "deposit" | "balance";
  contract_total?: number;
  due_now?: number;
  initial_due?: number;
  paid_amount?: number;
  balance_due?: number;
  balance_due_at_local?: string;
  balance_due_at_gmt?: string;
  balance_order_id?: number;
  balance_payment_url?: string;
  overdue?: boolean;
}
export interface CommerceQuote {
  id: string;
  version: number;
  status: "draft" | "issued" | "accepted" | "converted" | "expired";
  service: WorkspaceService;
  case_ref: CaseRef;
  customer: { name: string; email?: string; phone?: string };
  product_id?: number;
  date?: string;
  time?: string;
  people: number;
  adults_count?: number;
  children_count?: number;
  infants_count?: number;
  availability_capacity?: number;
  payment_option?: "full" | "deposit";
  /** School visit: levels, grades and workshops (ids from the brochure catalog). */
  school?: SchoolDetails;
  /** How the customer pays: date reservation fee, advance, on the day or all now. */
  payment_terms?: PaymentTerms;
  /** Seller discount: a percentage or a fixed amount. */
  discount?: QuoteDiscount | null;
  participants?: Record<string, unknown>[];
  items: {
    description: string;
    quantity: number;
    unit_price: number;
    total?: number;
  }[];
  currency: string;
  subtotal: number;
  total: number;
  expires_at: string;
  terms: string;
  availability_confirmed: boolean;
  public_url?: string;
  booking_id?: number;
  order_id?: number;
  payment_url?: string;
  /** Money the case paid before this quote (WooCommerce orders); only the rest is due. */
  prepaid?: number;
  created_at: string;
  updated_at: string;
  sent_at?: string | null;
  delivery?: {
    status: "accepted_by_mailer" | "failed" | "uncertain";
    recipient: string;
    attempted_at: string;
    accepted_at?: string | null;
  } | null;
  revisions?: CommerceQuote[];
  payment_plan?: AuthoritativePaymentPlan;
  payment_summary?: AuthoritativePaymentPlan;
  payment_status?: string | null;
  booking_status?: string;
  order_status?: string;
}
export type QuoteInput = Omit<
  CommerceQuote,
  | "id"
  | "version"
  | "status"
  | "subtotal"
  | "total"
  | "created_at"
  | "updated_at"
  | "public_url"
  | "booking_id"
  | "order_id"
  | "payment_url"
  | "sent_at"
  | "delivery"
  | "revisions"
  | "payment_plan"
  | "payment_summary"
  | "payment_status"
  | "booking_status"
  | "order_status"
>;
export interface CommerceCatalog {
  service?: WorkspaceService;
  products: { id: number; name: string; [key: string]: unknown }[];
  [key: string]: unknown;
}
export interface QuotePage {
  data: CommerceQuote[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    total_pages?: number;
  };
}
export interface CommerceResult {
  data?: CommerceQuote;
  status?: "pending_sync" | "failed";
  error?: { code?: string; message?: string };
  command_id?: string;
  message?: string;
}
export type ReportFilters = {
  from: string;
  to: string;
  service?: WorkspaceService;
};
export type QuoteFilters = {
  service?: WorkspaceService;
  case_kind?: CaseKind;
  case_id?: string;
  page?: number;
  per_page?: number;
  search?: string;
};
function query(values: object): string {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== "") params.set(key, String(value));
  });
  return params.toString();
}
// Fixtures activate only through an explicit local development flag. No runtime failure fallback.
const fixturesEnabled =
  import.meta.env.DEV && import.meta.env.VITE_USE_FIXTURES === "true";
export const workspaceApi = {
  /** `deferQuotes` returns the local dossier without waiting for WordPress quotes. */
  caseDetail: (kind: CaseKind, id: string, options: { deferQuotes?: boolean } = {}) =>
    fixturesEnabled
      ? fixtureCase(kind, id)
      : apiRequest<{ data: CaseDossier }>(
          `/api/v1/workspace/cases/${kind}/${encodeURIComponent(id)}${options.deferQuotes ? "?quotes=defer" : ""}`,
        ).then((result) => result.data),
  reports: (filters: ReportFilters) =>
    fixturesEnabled
      ? fixtureReports(filters)
      : apiRequest<{ data: WorkspaceReports }>(
          `/api/v1/workspace/reports?${query(filters)}`,
        ).then((result) => result.data),
};
/** A booking with money still to collect (API `/workspace/receivables`). */
export type Receivable = {
  sourceId: string;
  service: string;
  title: string;
  status: string;
  customerName: string;
  customerPhone?: string | null;
  date?: string | null;
  time?: string | null;
  people: number;
  currency: string;
  total: number;
  paid: number;
  pending: number;
  pendingDue?: string | null;
  pendingDayBefore: boolean;
  reservationFee: number;
  reservationFeePaid: number;
  overdue: boolean;
};
export type GuideSheet = {
  date: string;
  text: string;
  items: { sourceId: string; service: string; title: string; time: string | null; customerName: string; customerPhone: string | null; people: number; language: string | null; collect: number | null; currency: string }[];
};
const fixtureReceivables: Receivable[] = [
  { sourceId: "booking-524", service: "tour", title: "Tour Pirámides + Basílica", status: "confirmed", customerName: "María González", customerPhone: "+52 55 0000 0841", date: "2026-08-13", time: "09:00", people: 4, currency: "MXN", total: 5000, paid: 2500, pending: 2500, pendingDue: "2026-08-12", pendingDayBefore: true, reservationFee: 0, reservationFeePaid: 0, overdue: true },
];
export const collectionsApi = {
  receivables: () =>
    fixturesEnabled
      ? Promise.resolve(fixtureReceivables)
      : apiRequest<{ data: Receivable[] }>("/api/v1/workspace/receivables").then((r) => r.data),
  guideSheet: (date: string) =>
    fixturesEnabled
      ? Promise.resolve<GuideSheet>({ date, items: [], text: `*Servicios del ${date.split("-").reverse().join("/")}*\nSin servicios confirmados.` })
      : apiRequest<{ data: GuideSheet }>(`/api/v1/workspace/guide-sheet?date=${encodeURIComponent(date)}`).then((r) => r.data),
  guideSheetPdf: (date: string) =>
    downloadFile(`/api/v1/workspace/guide-sheet?date=${encodeURIComponent(date)}&format=pdf`, `Hoja del guia ${date}.pdf`),
};
export type BookingDocumentKind = "apartado" | "apartado-recibido" | "confirmacion" | "recibo";
/** Money received for a case before any quote, kept by WordPress as a paid WooCommerce order. */
/** What the advance did with the quote already sent: accepted and reserved, or why not. */
export type AutoReservation =
  | { status: "reserved"; quote_id: string; booking_id: number | null }
  | { status: "failed"; quote_id: string; message: string }
  | { status: "skipped" };
export interface CasePayment {
  order_id: number;
  amount: number;
  currency: string;
  customer_name: string;
  method: PaymentMethod;
  paid_on: string;
  note: string;
  /** Set once an accepted quote applied it to its booking. */
  quote_id: string;
  booking_id: number;
}
const caseQuery = (ref: CaseRef, service?: string | null) => query({ case_kind: ref.kind, case_id: ref.id, ...(service ? { service } : {}) });
export type PaymentSlipInput = { customer_name: string; service?: string; concept: "apartado" | "anticipo"; amount: number; date?: string; people?: number; teachers?: number; companions?: number; case_kind?: CaseKind; case_id?: string };
/** Another school group the same day, to warn before asking for or taking a payment. */
export interface DateConflict { kind: "hold" | "booking"; name: string; status: string; people: number | null }
export const dateHoldsApi = {
  conflicts: (date: string, ref: CaseRef) =>
    fixturesEnabled ? Promise.resolve({ data: [] as DateConflict[] }) : apiRequest<{ data: DateConflict[] }>(`/api/v1/workspace/date-holds/conflicts?${query({ date, case_kind: ref.kind, case_id: ref.id })}`),
  /** Holds the date provisionally for the case (one per case); the payment confirms it. */
  hold: (input: { case_ref: CaseRef; service?: string; date: string; customer_name: string; customer_phone?: string; people?: number; teachers?: number; companions?: number }) =>
    fixturesEnabled ? Promise.resolve({ data: { id: "fixture", date: input.date, status: "provisional" } }) : apiRequest<{ data: { id: string; date: string; status: string } }>("/api/v1/workspace/date-holds", { method: "POST", body: JSON.stringify(input) }),
};
export const commerceApi = {
  /** Payment slip PDF: the fee or advance with the bank details, without a quote or link. */
  downloadPaymentSlip: (input: PaymentSlipInput) =>
    downloadFile(`/api/v1/commerce/payment-slip?${query(input)}`, "Ficha de pago Ruta Magica.pdf"),
  sendPaymentSlip: (input: PaymentSlipInput, conversationId: string) =>
    apiRequest<{ data: { messageId: string } }>("/api/v1/commerce/payment-slip/whatsapp", {
      method: "POST",
      body: JSON.stringify({ ...input, conversation_id: conversationId }),
    }),
  /** Confirmation of the visit (the agreed quote with payments and rest); only after a payment. */
  downloadCaseConfirmation: (ref: CaseRef) =>
    downloadFile(`/api/v1/commerce/case-confirmation?${caseQuery(ref)}`, "Confirmacion Ruta Magica.pdf"),
  sendCaseConfirmation: (ref: CaseRef, conversationId: string) =>
    apiRequest<{ data: { messageId: string } }>("/api/v1/commerce/case-confirmation/whatsapp", {
      method: "POST",
      body: JSON.stringify({ case_kind: ref.kind, case_id: ref.id, conversation_id: conversationId }),
    }),
  casePayments: (ref: CaseRef) =>
    fixturesEnabled ? Promise.resolve({ data: [] as CasePayment[] }) : apiRequest<{ data: CasePayment[] }>(`/api/v1/commerce/case-payments?${caseQuery(ref)}`),
  recordCasePayment: (input: { command_id: string; case_ref: CaseRef; customer: { name: string; email?: string; phone?: string }; amount: number; method: PaymentMethod; paid_on?: string; note?: string }) =>
    apiRequest<{ data: CasePayment; auto_reservation?: AutoReservation }>("/api/v1/commerce/case-payments", { method: "POST", body: JSON.stringify(input) }),
  /** After sending a quote to a customer who already paid: accept it and create the booking. */
  reserveCase: (caseRef: CaseRef, commandId: string) =>
    apiRequest<{ data: AutoReservation }>("/api/v1/commerce/case-reserve", { method: "POST", body: JSON.stringify({ command_id: commandId, case_ref: caseRef }) }),
  downloadCaseReceipt: (orderId: number, ref: CaseRef, service?: string | null) =>
    downloadFile(`/api/v1/commerce/case-payments/${orderId}/receipt?${caseQuery(ref, service)}`, "Comprobante de pago Ruta Magica.pdf"),
  sendCaseReceipt: (orderId: number, ref: CaseRef, service: string | null | undefined, conversationId: string) =>
    apiRequest<{ data: { messageId: string } }>(`/api/v1/commerce/case-payments/${orderId}/receipt/whatsapp`, {
      method: "POST",
      body: JSON.stringify({ conversation_id: conversationId, case_kind: ref.kind, case_id: ref.id, ...(service ? { service } : {}) }),
    }),
  /** The PDF the customer receives, generated by the API from the WordPress quote. */
  downloadPdf: (id: string) => downloadFile(`/api/v1/commerce/quotes/${encodeURIComponent(id)}/pdf`, "Cotizacion Ruta Magica.pdf"),
  /** Sends the issued quote as a PDF in the customer's WhatsApp chat, with its link in the message. */
  sendWhatsApp: (id: string, conversationId: string) =>
    apiRequest<{ data: { messageId: string } }>(`/api/v1/commerce/quotes/${encodeURIComponent(id)}/whatsapp`, {
      method: "POST",
      body: JSON.stringify({ conversation_id: conversationId }),
    }),
  /** Booking confirmation without prices, for the balloon port. WordPress builds it on demand. */
  downloadOperatorConfirmation: (bookingId: string) =>
    downloadFile(`/api/v1/bookings/${encodeURIComponent(bookingId)}/operator-confirmation`, `Confirmacion RM-${bookingId} operador.pdf`),
  /** The customer's confirmation as it is generated today (the archived one is the copy that was sent). */
  downloadCurrentConfirmation: (bookingId: string) =>
    downloadFile(`/api/v1/bookings/${encodeURIComponent(bookingId)}/current-confirmation`, `Confirmacion RM-${bookingId}.pdf`),
  /** Reservation-fee request/receipt and booking confirmation PDFs, generated by the API. */
  downloadBookingDocument: (bookingId: string, kind: BookingDocumentKind) =>
    downloadFile(`/api/v1/bookings/${encodeURIComponent(bookingId)}/documents/${kind}`, `${kind} Ruta Magica.pdf`),
  sendBookingDocument: (bookingId: string, kind: BookingDocumentKind, conversationId: string) =>
    apiRequest<{ data: { messageId: string } }>(`/api/v1/bookings/${encodeURIComponent(bookingId)}/documents/${kind}/whatsapp`, {
      method: "POST",
      body: JSON.stringify({ conversation_id: conversationId }),
    }),
  command: (commandId: string) =>
    fixturesEnabled
      ? Promise.resolve(
          fixtureCommands.get(commandId) ?? {
            status: "pending_sync" as const,
            command_id: commandId,
          },
        )
      : apiRequest<CommerceResult>(`/api/v1/commerce/commands/${commandId}`),
  catalog: (service: WorkspaceService) =>
    fixturesEnabled
      ? Promise.resolve(fixtureCatalog(service))
      : apiRequest<{ data: CommerceCatalog }>(
          `/api/v1/commerce/catalog?${query({ service })}`,
        ).then((result) => result.data),
  quotes: (filters: QuoteFilters = {}) =>
    fixturesEnabled
      ? Promise.resolve(fixtureQuotePage(filters))
      : apiRequest<QuotePage>(`/api/v1/commerce/quotes?${query(filters)}`),
  quote: (id: string) =>
    fixturesEnabled
      ? Promise.resolve(fixtureQuote(id))
      : apiRequest<{ data: CommerceQuote }>(
          `/api/v1/commerce/quotes/${id}`,
        ).then((result) => result.data),
  create: (input: QuoteInput, commandId: string) =>
    fixturesEnabled
      ? Promise.resolve(fixtureSave(input, commandId))
      : apiRequest<CommerceResult>("/api/v1/commerce/quotes", {
          method: "POST",
          body: JSON.stringify({ ...input, command_id: commandId }),
        }),
  revise: (
    id: string,
    input: QuoteInput,
    expectedVersion: number,
    commandId: string,
  ) =>
    fixturesEnabled
      ? Promise.resolve(fixtureSave(input, commandId, id, expectedVersion))
      : apiRequest<CommerceResult>(`/api/v1/commerce/quotes/${id}`, {
          method: "PATCH",
          body: JSON.stringify({
            ...input,
            expected_version: expectedVersion,
            command_id: commandId,
          }),
        }),
  action: (
    id: string,
    action: "issue" | "accept" | "convert" | "send-email",
    expectedVersion: number,
    commandId: string,
    recipient?: string,
  ) =>
    fixturesEnabled
      ? Promise.resolve(fixtureAction(id, action, expectedVersion, commandId))
      : apiRequest<CommerceResult>(`/api/v1/commerce/quotes/${id}/${action}`, {
          method: "POST",
          body: JSON.stringify({
            expected_version: expectedVersion,
            command_id: commandId,
            ...(recipient ? { recipient } : {}),
          }),
        }),
};

const fixtureQuotes = new Map<string, CommerceQuote>();
const fixtureCommands = new Map<string, CommerceResult>();
function fixtureCatalog(service: WorkspaceService): CommerceCatalog {
  const names = {
    balloon: "Vuelo en globo",
    tour: "Tour privado",
    school: "Visita escolar",
  };
  return {
    service,
    products: [
      {
        id: service === "balloon" ? 101 : service === "tour" ? 102 : 103,
        name: names[service],
        service,
        price: service === "balloon" ? 2100 : service === "tour" ? 900 : 420,
        currency: "MXN",
        // Same shape as WordPress RXRM_Dossier_Pricing::public_config.
        ...(service === "balloon"
          ? { name: "Vuelo compartido desde CDMX", dossier: { package: "shared_transfer", language: "es", currency: "MXN", tiers: { 2: 3100, 3: 2920, 4: 2880, 5: 2860, 6: 2850, 7: 2850, 8: 2840, 9: 2840, 10: 2830, 11: 2830, 12: 2830, 13: 2830, 14: 2830, 15: 2820 } } }
          : service === "tour"
            ? { name: "Tour con transporte · español", dossier: { package: "tour_esencial", language: "es", currency: "MXN", tiers: { 2: 1310, 3: 1050, 4: 960, 5: 920, 6: 900, 7: 880, 8: 860, 9: 850, 10: 840, 11: 840, 12: 830, 13: 830, 14: 820, 15: 820 } } }
            : {}),
      },
    ],
    requirements: {
      school: [
        "Aceptación",
        "Fecha y disponibilidad validadas",
        "Número de participantes",
      ],
    },
  };
}
function fixtureQuotePage(filters: QuoteFilters): QuotePage {
  const matches = [...fixtureQuotes.values()].filter(
    (q) =>
      (!filters.service || q.service === filters.service) &&
      (!filters.case_kind || q.case_ref.kind === filters.case_kind) &&
      (!filters.case_id || q.case_ref.id === filters.case_id) &&
      (!filters.search ||
        q.customer.name.toLowerCase().includes(filters.search.toLowerCase())),
  );
  const page = filters.page ?? 1,
    perPage = filters.per_page ?? 25;
  return {
    data: structuredClone(matches.slice((page - 1) * perPage, page * perPage)),
    pagination: {
      page,
      per_page: perPage,
      total: matches.length,
      total_pages: Math.ceil(matches.length / perPage),
    },
  };
}
function fixtureQuote(id: string): CommerceQuote {
  const quote = fixtureQuotes.get(id);
  if (!quote) throw new Error("Cotización de demostración no encontrada.");
  return structuredClone(quote);
}
function fixtureSave(
  input: QuoteInput,
  commandId: string,
  id?: string,
  expectedVersion?: number,
): CommerceResult {
  const replay = fixtureCommands.get(commandId);
  if (replay) return structuredClone(replay);
  const existing = id ? fixtureQuote(id) : null;
  if (existing && existing.version !== expectedVersion)
    throw new ApiError("La cotización cambió; recarga antes de continuar.",409,"rxrm_quote_version_conflict");
  if (existing && (existing.status === "converted" || existing.booking_id))
    throw new ApiError("La cotización ya tiene una reserva vinculada.",409);
  const product = fixtureCatalog(input.service).products.find(p => p.id === input.product_id);
  const items = input.service === "school" ? input.items : [{description: `${product?.name ?? input.service} (demostración)`, quantity: input.people, unit_price:Number(product?.price ?? 0)}];
  const total = items.reduce(
      (sum, item) => sum + item.quantity * item.unit_price,
      0,
    ),
    now = new Date().toISOString();
  const quote: CommerceQuote = {
    ...structuredClone(input),
    items: items.map(item => ({...item,total:item.quantity*item.unit_price})),
    revisions: existing ? [...(existing.revisions ?? []), {...existing,revisions:[]}] : [],
    sent_at: null, delivery: null,
    id: id ?? crypto.randomUUID(),
    version: (existing?.version ?? 0) + 1,
    status: "draft",
    subtotal: total,
    total,
    created_at: existing?.created_at ?? now,
    updated_at: now,
  };
  fixtureQuotes.set(quote.id, quote);
  const result = { data: structuredClone(quote) };
  fixtureCommands.set(commandId, result);
  return result;
}
function fixtureAction(
  id: string,
  action: "issue" | "accept" | "convert" | "send-email",
  expectedVersion: number,
  commandId: string,
): CommerceResult {
  const replay = fixtureCommands.get(commandId);
  if (replay) return structuredClone(replay);
  const quote = fixtureQuote(id);
  if (quote.version !== expectedVersion)
    throw new ApiError("La cotización cambió; recarga antes de continuar.",409,"rxrm_quote_version_conflict");
  if (action === "send-email")
    throw new ApiError(
      "El envío real requiere WordPress. La demostración no envía correos.",400,"fixture_only"
    );
  if (action === "convert")
    throw new ApiError(
      "La conversión real requiere WordPress. La demostración no genera reservas ni cobros.",400,"fixture_only"
    );
  if (action === "issue" && quote.status !== "draft")
    throw new ApiError("La propuesta ya fue emitida.",409);
  if (action === "accept" && quote.status !== "issued")
    throw new ApiError("Primero emite la propuesta.",409);
  if(quote.service === "school" && (!quote.date || !quote.time || !quote.availability_confirmed || (quote.availability_capacity ?? 0)<quote.people)) throw new ApiError("Valida fecha, horario y cupo del grupo escolar.",409,"rxrm_quote_availability");
  quote.revisions = [...(quote.revisions ?? []), {...quote,revisions:[]}];
  quote.status = action === "issue" ? "issued" : "accepted";
  quote.version += 1;
  quote.updated_at = new Date().toISOString();
  fixtureQuotes.set(id, quote);
  const result = { data: structuredClone(quote) };
  fixtureCommands.set(commandId, result);
  return result;
}
async function fixtureCase(kind: CaseKind, id: string): Promise<CaseDossier> {
  const { fixtureBookings, fixtureOrders, fixtureLeads } = await import(
    "../dev/fixtures"
  );
  const { crmApi } = await import("./crm");
  const opportunities =
    kind === "opportunity"
      ? (await crmApi.listOpportunities(1, { attention: "all" })).data
      : [];
  const opportunity = opportunities.find((item) => item.id === id);
  let booking =
    kind === "booking"
      ? fixtureBookings.find((item) => item.id === id || item.reference === id)
      : undefined;
  let order =
    kind === "order"
      ? fixtureOrders.find((item) => item.id === id || item.number === id)
      : undefined;
  const lead =
    kind === "lead" ? fixtureLeads.find((item) => item.id === id) : undefined;
  if (booking)
    order = fixtureOrders.find(
      (item) =>
        item.number === booking!.orderNumber ||
        item.bookingId === booking!.id ||
        item.bookingId === booking!.reference,
    );
  if (order)
    booking = fixtureBookings.find(
      (item) =>
        item.id === order!.bookingId ||
        item.reference === order!.bookingId ||
        item.orderNumber === order!.number,
    );
  const primary = opportunity ?? booking ?? order ?? lead;
  if (!primary) throw new Error("Expediente de demostración no encontrado.");
  const name =
    opportunity?.customerName ??
    booking?.customer ??
    order?.customer ??
    lead?.customer ??
    "Cliente";
  const bookingEntity: CaseEntity | undefined = booking
    ? {
        ...booking,
        id: booking.id,
        source_id: booking.id,
        status: booking.status,
        service_type: booking.service,
        booking_date: booking.date,
        booking_time: booking.time,
        people_count: booking.guests,
        customer_name: booking.customer,
        source_version: booking.version,
        attendance_status: booking.attended ? "attended" : "pending",
      }
    : undefined;
  const orderEntity: CaseEntity | undefined = order
    ? {
        ...order,
        id: order.id,
        source_id: order.id,
        order_number: order.number,
        payment_method_title: order.paymentMethod,
        paid_at: order.paymentStatus === "paid" ? order.createdAt : null,
      }
    : undefined;
  const leadEntity: CaseEntity | undefined = lead
    ? {
        ...lead,
        id: lead.id,
        source_id: lead.id,
        service_type: lead.service,
        customer_name: lead.customer,
        requested_date: lead.requestedDate,
        people_count: lead.peopleCount,
        last_note: lead.notes.join("\n"),
      }
    : undefined;
  const quotes = fixtureQuotePage({ case_kind: kind, case_id: id }).data;
  const missing =
    !booking && !lead?.requestedDate && !opportunity?.requestedDate
      ? ["Fecha de visita"]
      : [];
  return {
    key: { kind, id },
    service: primary.service,
    title: name,
    customer: {
      name,
      ...(opportunity ? { id: opportunity.customerId } : {}),
      email: booking?.email ?? order?.email ?? lead?.email ?? "",
      phone: booking?.phone ?? order?.phone ?? lead?.phone ?? "",
    },
    customerContext: {
      customerId: opportunity?.customerId ?? null,
      description:
        "Datos de demostración. Se muestran únicamente los vínculos explícitos de este expediente.",
    },
    status: {
      commercial: opportunity?.stage ?? lead?.status ?? null,
      payment: order?.paymentStatus ?? null,
      operational: booking?.status ?? null,
    },
    nextAction: missing.length
      ? "Completar fecha de visita"
      : booking?.status === "confirmed"
        ? "Preparar visita y asistencia"
        : quotes.some((q) => q.status === "accepted")
          ? "Validar disponibilidad y convertir cotización"
          : "Preparar propuesta",
    missing,
    associatedIds: {
      opportunity: opportunity ? [opportunity.id] : [],
      booking: booking ? [booking.id] : [],
      order: order ? [order.id] : [],
      lead: lead ? [lead.id] : [],
      thread: opportunity?.threadId ? [opportunity.threadId] : [],
      quote: quotes.map((q) => String(q.id)),
    },
    opportunity: opportunity
      ? {
          ...opportunity,
          id: opportunity.id,
          service_type: opportunity.service,
          customer_name: opportunity.customerName,
          people_count: opportunity.peopleCount,
          requested_date: opportunity.requestedDate,
        }
      : null,
    bookings: bookingEntity ? [bookingEntity] : [],
    orders: orderEntity ? [orderEntity] : [],
    leads: leadEntity ? [leadEntity] : [],
    conversations: opportunity?.threadId
      ? [
          {
            id: opportunity.threadId,
            channel: opportunity.channel,
            subject: "Seguimiento de propuesta",
            state: "open",
          },
        ]
      : [],
    payments:
      order?.paymentStatus === "paid"
        ? [
            {
              id: `payment-${order.id}`,
              status: "paid",
              provider: order.paymentMethod,
              amount: order.total,
              currency: order.currency,
              paid_at: order.createdAt,
            },
          ]
        : [],
    participants:
      booking?.passengers.map((person) => ({
        ...person,
        id: person.id,
        attendee_type: person.type,
        weight_kg: person.weightKg,
      })) ?? [],
    documents: [],
    quotes,
    timeline: (booking?.timeline ?? order?.timeline ?? []).map((event) => ({
      ...event,
      id: event.id,
      activity_type: event.title,
      body: event.description,
      actor_label: event.actor,
      occurred_at: event.occurredAt,
    })),
    permissions: { finance: true, crm: true, commerceWrite: true },
    commerceSync: "available",
    // Demo chat for every case with a phone; the demo conversation lives in CaseChat.
    whatsappConversationId: "00000000-0000-4000-8000-000000000099",
  };
}
async function fixtureReports(
  filters: ReportFilters,
): Promise<WorkspaceReports> {
  const { fixtureBookings, fixtureOrders, fixtureAudit } = await import(
    "../dev/fixtures"
  );
  const bookings = fixtureBookings.filter(
    (b) =>
      (!filters.service || b.service === filters.service) &&
      b.date >= filters.from &&
      b.date <= filters.to,
  );
  const orders = fixtureOrders.filter(
    (o) =>
      o.paymentStatus === "paid" &&
      (!filters.service || o.service === filters.service) &&
      o.createdAt.slice(0, 10) >= filters.from &&
      o.createdAt.slice(0, 10) <= filters.to,
  );
  return {
    period: {
      ...filters,
      service: filters.service ?? null,
      timezone: "America/Mexico_City",
    },
    commercial: {
      won: 0,
      lost: 0,
      conversionRate: null,
      denominator: 0,
      manualWon: 0,
      officialWon: 0,
    },
    officialReservations: {
      confirmed: bookings.filter((b) => b.status === "confirmed").length,
      completed: bookings.filter((b) => b.status === "completed").length,
      passengers: bookings
        .filter((b) => b.status === "confirmed" || b.status === "completed")
        .reduce((sum, b) => sum + b.guests, 0),
    },
    payments: orders.length
      ? [
          {
            currency: "MXN",
            transactions: orders.length,
            received: orders.reduce((sum, o) => sum + o.total, 0),
            refunded: 0,
            net: orders.reduce((sum, o) => sum + o.total, 0),
          },
        ]
      : [],
    followups: { overdue: 0 },
    response: { conversations: 0, averageMinutes: null },
    activity: fixtureAudit
      .filter(
        (a) =>
          a.createdAt.slice(0, 10) >= filters.from &&
          a.createdAt.slice(0, 10) <= filters.to,
      )
      .map((a) => ({ action: a.action, count: 1 })),
  };
}
